import { useEffect, useRef, useState, type RefObject } from "react";
import { useLocation } from "react-router-dom";

/**
 * Brings the element addressed by the URL hash into view once the page has
 * rendered. A targeted <details> disclosure, or the disclosure containing a
 * targeted descendant, is opened first so linked records are visible.
 * Scrolling is instant, so the reduced-motion preference holds.
 */
export function useScrollToHash(): void {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const element = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!element) return;
    /* Tag check rather than instanceof, so the element's own realm never matters. */
    if (element.tagName === "DETAILS") (element as HTMLDetailsElement).open = true;
    const disclosure = element.closest<HTMLDetailsElement>("details:not([open])");
    if (disclosure) disclosure.open = true;
    if (typeof element.scrollIntoView === "function") element.scrollIntoView();
  }, [hash]);
}

const baseDocumentTitle = "Accenture GroundWorks — the foundation for agentic source-to-pay";

/**
 * Stamps a per-route document title so tabs, history, bookmarks and the
 * assistive-technology page announcement name the page, not just the site.
 * The home page passes nothing and keeps the full base title.
 */
export function usePageTitle(title?: string, enabled = true): void {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;
    document.title = title ? `${title} · Accenture GroundWorks` : baseDocumentTitle;
  }, [title, enabled]);
}

/**
 * One-shot scroll reveal for the sanctioned entrance moves. The class is added
 * only when the visitor allows motion, the browser can observe the element and
 * the element has been seen, and it starts an animation rather than lifting a
 * hidden state: nothing is pre-hidden, so a page without script, with reduced
 * motion, in print or captured whole reads as the finished page. Once seen, the
 * class never leaves: every move plays once.
 */
/**
 * Counts a governed figure up from zero once its band is revealed. The value
 * renders complete from the first paint — no script, reduced motion, print
 * and capture all read the finished figure — and the count runs only when
 * the visitor allows motion, plays once, and lands exactly on the value.
 */
export function useCountUp(value: number, active: boolean): number {
  const [display, setDisplay] = useState(value);
  const played = useRef(false);
  useEffect(() => {
    if (!active || played.current) return;
    if (typeof window.matchMedia !== "function") return;
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
    if (typeof window.requestAnimationFrame !== "function") return;
    played.current = true;
    const duration = 700;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(Math.round(value * eased));
      if (progress < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [active, value]);
  return display;
}

export function useRevealOnce<T extends HTMLElement>(): { ref: RefObject<T | null>; revealClass: string } {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    if (typeof window.matchMedia !== "function") return;
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, revealClass: seen ? " apf-reveal--seen" : "" };
}
